import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { ArrowMoveDownRightIcon, ArrowUp01Icon, Attachment01Icon, Mic02Icon, PencilIcon } from "hugeicons-react";
import { ChevronDown } from "lucide-react";
import { stripMessageContent } from "../../helpers/chatHelper";
import { AIResponseRenderer } from "../../handler/TextRenderer/LLMTextRenderer";
import { CopyButton } from '../Layout/Buttons/CopyButton';
import { RegenerateButton } from "../Layout/Buttons/RegenerateButton";
import { ConvexMessage } from "../../types/message";
import '../../Styles/Message/MessageBubble.css';
import { useAuth } from "../../contexts/AuthContext";
import { useChatContext } from "../../contexts/AppDataContext";
import { useTheme } from "../../contexts/WindowContext";
import { useModelContext, AUTO_LOADING_MODEL } from "../../contexts/ModelContext";
import { chatConfig } from "../../config/chatConfig";
import BranchToggle from "../Layout/MiniDisplays/BranchToggle";
import { MessageAttachments } from "../Layout/MessageAttachments";
import { SearchItemContainer } from "../Layout/Items/SearchItemContainer";
import { HoverToolTip } from "../Layout/Items/HoverToolTip";
import { Mode } from "../../types/mode";
import { Model } from "../../types/model";
import { useTextSelection } from "../../hooks/useTextSelection";
import { QuoteMarker } from "../Layout/Items/QuoteMarker";
import { MessageTTSMenuItems } from "./MessageTTSMenuItems";
import { MessageRatingButtons } from "./MessageRatingButtons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuHeader,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { EllipsisIcon } from "../../assets/CustomSvgs";
import { PromptFixButton } from "../Layout/Buttons/PromptFixButton";
import { ResponseSettingsPicker } from "../Layout/ResponseSettingsPicker";
import { useResourceContext } from "../../contexts/ResourceContext";
import { splitQuotedMessage } from "../../helpers/quotedMessage";
import { ImageFileRecord, TextFileRecord } from "../../types/files";
import { FileCard } from "../Layout/FileCard";
import { ImageAttachmentFileCard } from "../Layout/Input-Area/attachmentImageCard";
import { ACCEPTED_FILE_TYPES } from "../../config/AcceptedFileTypes";

const EDIT_ACCEPT_STRING = Object.entries(ACCEPTED_FILE_TYPES)
  .flatMap(([mimeType, extensions]) => [mimeType, ...extensions])
  .join(',');

function isSameLocalCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatMessageSentAt(message: ConvexMessage): { label: string; iso: string } | null {
  const raw = message.timestamp ?? message._creationTime;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return null;
  }

  const now = new Date();
  const timePart = new Intl.DateTimeFormat(undefined, { timeStyle: "short" }).format(d);

  const startOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);

  let label: string;
  if (isSameLocalCalendarDay(d, now)) {
    label = `Today, ${timePart}`;
  } else if (isSameLocalCalendarDay(d, startOfYesterday)) {
    label = `Yesterday, ${timePart}`;
  } else if (d.getFullYear() === now.getFullYear()) {
    const datePart = new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
    }).format(d);
    label = `${datePart}, ${timePart}`;
  } else {
    const datePart = new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(d);
    label = `${datePart}, ${timePart}`;
  }

  return { label, iso: d.toISOString() };
}

interface EditActions {
  handleEditSubmit: (message: ConvexMessage) => boolean;
  setEditContent: (content: string) => void;
}

interface MessageActions {
  RegenerateMessage: (messageId: string, mode: Mode) => Promise<void>;
}

interface RenderFunctions {
  EditArea: React.ComponentType;
  editContent: string;
  setEditContent: (content: string) => void;
  editMode: Mode;
  setEditMode: (mode: Mode) => void;
  editCurrentModel: Model | null;
  setEditCurrentModel: (model: Model | null) => void;
  prepareEditContent: (content: string, message?: ConvexMessage) => void;
  editTextFiles: TextFileRecord[];
  editImageFiles: ImageFileRecord[];
  addEditFiles: (files: File[]) => void;
  removeEditAttachment: (id: string) => void;
}

export interface MessageBubbleProps {
  message: ConvexMessage;
  handleResourceClick: (resourceTitle: string, messageId: string) => void;
  editActions: EditActions;
  messageActions: MessageActions;
  currentMode: Mode;
  renderFunctions: RenderFunctions;
  handleBranchChange: (branchId: number) => void;
  branches: number[];
  /** Someone else's shared chat: no edit, regenerate, rating or quoting. */
  readOnly?: boolean;
}

interface AIMessageProps {
  message: ConvexMessage;
  handleResourceClick: (resourceTitle: string, messageId: string) => void;
  currentMode: Mode;
  renderFunctions: RenderFunctions;
  messageActions: MessageActions;
  hasChatsLeft: boolean | null;
  resolvedTheme?: string;
  forwardedRef?: React.Ref<HTMLDivElement>;
  handleBranchChange: (branchId: number) => void;
  branches: number[];
  readOnly?: boolean;
}

interface UserMessageProps {
  message: ConvexMessage
  handleResourceClick: (resourceTitle: string, messageId: string) => void;
  currentMode: Mode;
  hasChatsLeft: boolean | null;
  editActions?: EditActions;
  renderFunctions?: RenderFunctions;
  forwardedRef?: React.Ref<HTMLDivElement>;
  handleBranchChange: (branchId: number) => void;
  branches: number[];
  editContent: string;
  setEditContent: (content: string) => void;
  editMode: Mode;
  setEditMode: (mode: Mode) => void;
  editCurrentModel: Model | null;
  setEditCurrentModel: (model: Model | null) => void;
  prepareEditContent: (content: string, message?: ConvexMessage) => void;
  readOnly?: boolean;
}

const MessageBubbleComponent = React.forwardRef<HTMLDivElement, MessageBubbleProps>(({
    message,
    handleResourceClick,
    editActions,
    messageActions,
    currentMode,
    renderFunctions,
    handleBranchChange,
    branches,
    readOnly = false,
  }, ref) => {
    const auth = useAuth();
    const { resolvedTheme } = useTheme();
    const hasChatsLeft = auth.chatUsageData?.remaining === null || (auth.chatUsageData?.remaining !== undefined && auth.chatUsageData.remaining > 0);

    return message.isAi ? (
        <AIMessage 
            message={message}
            handleResourceClick={handleResourceClick}
            currentMode={currentMode}
            renderFunctions={renderFunctions}
            messageActions={messageActions}
            hasChatsLeft={hasChatsLeft}
            resolvedTheme={resolvedTheme}
            forwardedRef={ref}
            handleBranchChange={handleBranchChange}
            branches={branches}
            readOnly={readOnly}
        />
    ) : (
        <UserMessage 
            message={message}
            handleResourceClick={handleResourceClick}
            editActions={editActions}
            renderFunctions={renderFunctions}
            currentMode={currentMode}
            hasChatsLeft={hasChatsLeft}
            forwardedRef={ref}
            handleBranchChange={handleBranchChange}
            branches={branches}
            editContent={renderFunctions.editContent}
            setEditContent={renderFunctions.setEditContent}
            editMode={renderFunctions.editMode}
            setEditMode={renderFunctions.setEditMode}
            editCurrentModel={renderFunctions.editCurrentModel}
            setEditCurrentModel={renderFunctions.setEditCurrentModel}
            prepareEditContent={renderFunctions.prepareEditContent}
            readOnly={readOnly}
        />
    );
});

const AIMessage = React.memo<AIMessageProps>(({
  message,
  handleResourceClick,
  currentMode,
  messageActions,
  hasChatsLeft,
  resolvedTheme,
  forwardedRef,
  handleBranchChange,
  branches,
  readOnly = false,
}) => {
  const [animate, setAnimate] = useState(true);
  useEffect(() => {
    setAnimate(false);
  }, []);
  const auth = useAuth();
  const { setQuotedContent, isIncognito } = useChatContext();
  const { currentChatResources, expandedResource, isResourcesVisible } = useResourceContext();
  const messageRef = useRef<HTMLDivElement | null>(null);
  const { selection, clearSelection } = useTextSelection(messageRef);

  // Combine refs
  const combinedRef = useCallback((node: HTMLDivElement | null) => {
    if (messageRef) {
      (messageRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    }
    if (forwardedRef) {
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else {
        (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    }
  }, [forwardedRef]);

  const memoizedHandleResourceClick = useCallback(
    (resourceTitle: string, msgId: string) => {
      handleResourceClick(resourceTitle, msgId);
    },
    [handleResourceClick]
  );

  const isEmpty = message.content.trim() === '' && !message.isGenerating;

  // Prepare copy-safe message content and remove resource_viewing tags.
  let strippedContent = stripMessageContent(message.content);
  // Remove resource viewing tags (with or without newline) and trim whitespace
  strippedContent = strippedContent.replace(/<resource_viewing>.*?<\/resource_viewing>/g, '').trim();

  const userFriendlyMessage = {
    ...message,
    content: strippedContent
  };

  const currentBranchIndex = branches.indexOf(message.branchId || 1) + 1;

  const processBranchChange = (branchIndex: number) => {
    const branchId = branches[branchIndex - 1];
    handleBranchChange(branchId);
  };

  const lockFooter = branches.length > 1;

  const handleQuoteSelection = () => {
    if (selection.text) {
      setQuotedContent(selection.text);
      clearSelection();
    }
  };

  const sentAtDisplay = useMemo(
    () => formatMessageSentAt(message),
    [message]
  );

  const resourcePreviewState = useMemo(() => ({
    resources: currentChatResources,
    viewedResource: expandedResource,
    isResourceViewVisible: isResourcesVisible,
  }), [currentChatResources, expandedResource, isResourcesVisible]);

  return (
    <div
      key={message._id}
      className={`message ai${animate ? ' animate' : ''}`}
      ref={combinedRef}
    >
      {isEmpty ? (
        <div className="empty-message">
          <p>No response</p>
        </div>
      ) : (
        <>
          <AIResponseRenderer
            content={message.content}
            messageId={message._id}
            handleResourceClick={memoizedHandleResourceClick}
            isGenerating={message.isGenerating || false}
            resourcePreviewState={resourcePreviewState}
          />
        </>
      )}

      {!message.isGenerating && (
        <>
          <div className="message-footer">
            {branches.length > 1 && (
              <div className="message-info">
                <BranchToggle
                  currentBranch={currentBranchIndex}
                  totalBranches={branches.length}
                  onBranchChange={processBranchChange}
                />
              </div>
            )}
      

            <div className={`message-actions ${lockFooter ? 'is-locked' : ''}`}>
              <CopyButton item={userFriendlyMessage} size={17} />
              {hasChatsLeft && !readOnly && (
                <RegenerateButton
                  message={message}
                  onRegenerate={async (msgId, mode) => {
                    await messageActions.RegenerateMessage(msgId, mode);
                  }}
                />
              )}
              {(auth.user || !isIncognito) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="action-button"
                      aria-label="More message actions"
                    >
                      <EllipsisIcon aria-hidden className="shrink-0" width={17} height={17} />
                      <HoverToolTip text="More Actions" position="bottom" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" side="bottom" sideOffset={6}>
                    {sentAtDisplay ? (
                      <>
                        <DropdownMenuHeader>
                          <time dateTime={sentAtDisplay.iso}>{sentAtDisplay.label}</time>
                        </DropdownMenuHeader>
                      </>
                    ) : null}
                    {auth.user && (
                      <MessageTTSMenuItems
                        messageId={message._id}
                        messageContent={message.content}
                        isEmpty={isEmpty}
                      />
                    )}
                    {!isIncognito && !readOnly && (
                      <MessageRatingButtons
                        message={message}
                        currentMode={currentMode}
                        onRegenerate={messageActions.RegenerateMessage}
                        layout="menu"
                      />
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            <div className="message-indicators">
              {!message.isGenerating && (
                <SearchItemContainer
                  searchItems={message.searchItems || []}
                />
              )}
            </div>
          </div>
        </>
      )}

      <QuoteMarker
        x={selection.x}
        y={selection.y}
        onClick={handleQuoteSelection}
        isVisible={selection.isVisible && !readOnly}
      />
    </div>
  );
});

const UserMessage = React.memo<UserMessageProps>(({
  message,
  handleResourceClick,
  hasChatsLeft,
  editActions,
  renderFunctions,
  forwardedRef,
  handleBranchChange,
  branches,
  editContent,
  setEditContent,
  editMode,
  setEditMode,
  editCurrentModel,
  setEditCurrentModel,
  prepareEditContent,
  readOnly = false,
}) => {
  // Animate only on initial mount, skip on updates (e.g., branch changes)
  const [animate, setAnimate] = useState(true);
  useEffect(() => {
    setAnimate(false);
  }, []);
  const auth = useAuth();
  const { currentModel } = useModelContext();
  const { editingMessageId, setEditingMessageId } = useChatContext();
  const isEditing = editingMessageId === message._id;
  const [isEditOpening, setIsEditOpening] = useState(false);
  const editOpeningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const messageModel: Model|null = auth.modelRegistry && message.model ? auth.modelRegistry[message.model] : currentModel;

  useEffect(() => {
    if (!isEditing) {
      setIsEditOpening(false);
    }
  }, [isEditing]);

  useEffect(() => {
    return () => {
      if (editOpeningTimer.current) {
        clearTimeout(editOpeningTimer.current);
      }
    };
  }, []);
  
  const startEditing = () => {
    if (editOpeningTimer.current) {
      clearTimeout(editOpeningTimer.current);
    }
    setIsEditOpening(true);
    setEditingMessageId(message._id);
    prepareEditContent(message.content, message)
    if(message.mode) {
      setEditMode(message.mode);
    }
    setEditCurrentModel(messageModel);
    editOpeningTimer.current = setTimeout(() => {
      setIsEditOpening(false);
    }, 320);
  };

  const stopEditing = () => {
    if (editOpeningTimer.current) {
      clearTimeout(editOpeningTimer.current);
    }
    setIsEditOpening(false);
    setEditingMessageId(null);
  };
  const { quotedText, messageText } = useMemo(
    () => splitQuotedMessage(message.content || ''),
    [message.content]
  );
  
  const MAX_CHARS_FOR_TRUNCATION_CHECK = 1000; // Use a character check to decide *if* truncation UI is needed
  const isPotentiallyLong = messageText.length > MAX_CHARS_FOR_TRUNCATION_CHECK;
  const [isExpanded, setIsExpanded] = useState(false);

  // We always render the full content now, CSS handles the truncation visually
  const contentToDisplay = messageText;

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const userFriendlyMessage = {
    ...message,
    content: quotedText ? `${quotedText}\n\n${messageText}` : messageText
  }

  const quoteDisplay = quotedText ? (
    <div className="user-message-quote" aria-label="Quoted text">
      <span className="user-message-quote-icon" aria-hidden>
        <ArrowMoveDownRightIcon size={20} />
      </span>
      <span className="user-message-quote-text">{quotedText}</span>
    </div>
  ) : null;

  const currentBranchIndex = branches.indexOf(message.branchId || 1) + 1;

  const processBranchChange = (branchIndex: number) => {
    const branchId = branches[branchIndex - 1];
    handleBranchChange(branchId);
  }

  if (isEditing && editActions && renderFunctions) {
    const { editTextFiles, editImageFiles, addEditFiles, removeEditAttachment } = renderFunctions;
    const hasEditAttachments = editTextFiles.length > 0 || editImageFiles.length > 0;
    return (
      <div className={`user-message-wrapper editing${isEditOpening ? ' opening-edit' : ''}`}>
        {quoteDisplay}
        <div 
          key={message._id}
          className={`message user editing${animate ? ' animate' : ''}`}
          ref={forwardedRef}
        >
          <form className="edit-message-form" onSubmit={(e) => { e.preventDefault(); if (editActions.handleEditSubmit(message)) setEditingMessageId(null); }}>
            {hasEditAttachments && (
              <div className="attached-files-section edit-attached-files" aria-label="Attached files">
                {editTextFiles.map(file => (
                  <FileCard key={file.id} file={file} onRemove={() => removeEditAttachment(file.id)} />
                ))}
                {editImageFiles.map(file => (
                  <ImageAttachmentFileCard key={file.id} imageFile={file} onRemove={() => removeEditAttachment(file.id)} />
                ))}
              </div>
            )}
            <div className="edit-input-shell">
              <renderFunctions.EditArea />
            </div>
            <div className={`message-footer ${isEditing ? 'editing' : 'not-editing'}`}>
              <div className="edit-settings">
                <ResponseSettingsPicker
                  currentMode={editMode}
                  setMode={setEditMode}
                  currentModel={editCurrentModel ?? AUTO_LOADING_MODEL}
                  setCurrentModel={setEditCurrentModel}
                />
                <PromptFixButton input={editContent} setInput={setEditContent} isEditing={true} />
                {auth.user && (
                  <label className="edit-attach-button" aria-label="Add attachments">
                    <Attachment01Icon size={18} />
                    <input
                      type="file"
                      multiple
                      accept={EDIT_ACCEPT_STRING}
                      hidden
                      onChange={(e) => {
                        addEditFiles(Array.from(e.target.files || []));
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}
              </div>
              <div className="edit-actions">
                <button type="button" onClick={stopEditing} className="cancel-edit-button">Cancel</button>
                <button type="submit" className="save-edit-button">Save</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if(contentToDisplay.trim().length === 0) {
    return (
      <div className="user-message-wrapper">
        <MessageAttachments message={message} handleResourceClick={handleResourceClick} />
        {quoteDisplay}
      </div>
    );
  }
  
  return (
    <div className="user-message-wrapper">
      <MessageAttachments 
        message={message}
        handleResourceClick={handleResourceClick}
      />
      {quoteDisplay}
      <div 
        key={message._id}
        className={`message user${animate ? ' animate' : ''}`}
        ref={forwardedRef}
      >
        <div className="user-message-container">
          <div className="user-message-content">
            <div className="user-message-left">
              <div className={`message-content-wrapper ${isPotentiallyLong && !isExpanded ? 'truncated' : ''}`}>
                <div className="message-content">
                  {contentToDisplay}
                </div>
                {isPotentiallyLong && !isExpanded && (
                  <div className="show-more-overlay" onClick={toggleExpand}>
                    <ChevronDown size={18} aria-hidden />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={`user-message-footer ${message.isVoice ? 'voice-message' : ''}`}>
        {message.isVoice ? (
          <Mic02Icon size={14} />
        ) : (
          <>
            {branches.length > 1 && (
              <BranchToggle 
                currentBranch={currentBranchIndex} 
                totalBranches={branches.length} 
                onBranchChange={processBranchChange} 
              />
            )}
            {!isEditing && !readOnly && hasChatsLeft && chatConfig.features && chatConfig.features.editMessage !== false && (
              <button
                className="action-button"
                title="Edit message"
                onClick={(e) => {
                  e.preventDefault();
                  startEditing();
                }}
              >
                <PencilIcon size={15} />
                <HoverToolTip text="Edit" position="bottom" />
              </button>
            )}
            {isPotentiallyLong && isExpanded && (
              <button onClick={toggleExpand} className="action-button show-less-button">
                <ArrowUp01Icon size={18} />
                <HoverToolTip text="Show Less" position="bottom" />
              </button>
            )}
            <CopyButton 
              item={userFriendlyMessage} 
              useRichCopy={false}
              showHoverText={true}
              className="action-button"
            />
          </>
        )}
      </div>
    </div>
  );
});

export const MessageBubble = React.memo(MessageBubbleComponent);

MessageBubble.displayName = 'MessageBubble';
AIMessage.displayName = 'AIMessage';
UserMessage.displayName = 'UserMessage';
